import React from 'react';
import PropTypes from 'prop-types';
import { Select } from '../UIComponents';
import { FaCheck } from 'react-icons/fa6';

const TABLE_RATINGS = ['Table B', 'Table C', 'Table D', 'Table E', 'Table F', 'Table H'];

/**
 * LifePlanSection Component
 * Life insurance plan configuration fields for the Plan column
 * Handles complex conditional logic based on:
 * - Solve For (Face Amount vs Premium)
 * - Product Type (IUL vs other products)
 */
const LifePlanSection = ({ formData, onInputChange, hasQuoteByPremium, showFaceAmountPopup, setShowFaceAmountPopup, styles }) => {
  const lifeSolveOptions = hasQuoteByPremium ? ['Face Amount', 'Premium'] : ['Premium'];
  const lifeSolveFor = lifeSolveOptions?.includes(formData?.solveFor) ? formData?.solveFor : lifeSolveOptions.length > 1 ? lifeSolveOptions[1] : lifeSolveOptions[0];

  // Check if any selected products are IUL
  const hasIULSelected = (formData.selectedProductTypes || []).some(pt => {
    const productLower = (pt || '').toLowerCase();
    return productLower.includes('iul') || productLower.includes('indexed universal');
  });

  return (
    <div style={styles.formGrid}>
      {/* Solve For */}
      <div style={styles.formGroup} data-itemid='solve-for-wrapper'>
        <div style={styles.row} data-itemid='solve-for-label-wrapper'>
          <label style={styles.label} data-itemid='solve-for-label'>
            Solve For
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='solve-for-label-required'>
            *
          </label>
        </div>
        <div style={styles.buttonGroup} data-itemid='solve-for-button-group'>
          {lifeSolveOptions.map(option => (
            <button key={option} onClick={() => onInputChange('solveFor', option)} style={styles.button(lifeSolveFor === option)} type='button' data-itemid={`solve-for-button-${option}`}>
              {option}
              {lifeSolveFor === option && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} data-itemid={`solve-for-button-${option}-check`} />}
            </button>
          ))}
        </div>
      </div>

      {/* Conditional: Face Amount(s) or Premium Amount */}
      {lifeSolveFor === 'Premium' ? (
        <>
          {/* When solving for Premium, show Face Amount(s) - multiple values */}
          <div style={styles.formGroup} data-itemid='face-amounts-wrapper'>
            <div style={styles.row} data-itemid='face-amounts-label-wrapper'>
              <label style={styles.label} data-itemid='face-amounts-label'>
                Face Amount(s)
              </label>
              <label style={{ ...styles.label, color: 'red' }} data-itemid='face-amounts-label-required'>
                *
              </label>
            </div>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  padding: '6px 8px',
                  minHeight: '38px',
                  alignItems: 'center'
                }}
                data-itemid='face-amounts-tag-wrapper'
              >
                {Array.isArray(formData.faceAmounts) &&
                  formData.faceAmounts.map((amount, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#333',
                        color: '#fff',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #333'
                      }}
                      data-itemid={`face-amount-tag-${amount}`}
                    >
                      ${parseInt(amount).toLocaleString()}
                      <span
                        onClick={() => {
                          const newAmounts = [...formData.faceAmounts];
                          newAmounts.splice(index, 1);
                          onInputChange('faceAmounts', newAmounts);
                        }}
                        style={{
                          marginLeft: '6px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                        data-itemid={`face-amount-tag-${amount}-remove-button`}
                      >
                        ×
                      </span>
                    </div>
                  ))}

                <input
                  data-itemid='face-amounts-input'
                  disabled={Array.isArray(formData.faceAmounts) && formData.faceAmounts.length >= 3}
                  onBlur={e => {
                    setShowFaceAmountPopup(false);
                    if (Array.isArray(formData.faceAmounts) && formData.faceAmounts.length >= 3) {
                      return;
                    }
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value) {
                      const amount = parseInt(value);
                      if (amount > 0) {
                        const newAmounts = [...(formData.faceAmounts || []), amount];
                        onInputChange('faceAmounts', newAmounts);
                        e.target.value = '';
                      }
                    }
                  }}
                  onFocus={() => setShowFaceAmountPopup(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      if (Array.isArray(formData.faceAmounts) && formData.faceAmounts.length >= 3) {
                        return;
                      }
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value) {
                        const amount = parseInt(value);
                        if (amount > 0) {
                          const newAmounts = [...(formData.faceAmounts || []), amount];
                          onInputChange('faceAmounts', newAmounts);
                          e.target.value = '';
                        }
                      }
                    }
                  }}
                  placeholder={Array.isArray(formData.faceAmounts) && formData.faceAmounts.length > 0 ? '' : 'Enter face amounts...'}
                  style={{
                    border: 'none',
                    outline: 'none',
                    flex: '1',
                    minWidth: '60px',
                    fontSize: '14px',
                    background: 'transparent',
                    opacity: Array.isArray(formData.faceAmounts) && formData.faceAmounts.length >= 3 ? 0.5 : 1
                  }}
                  type='text'
                />
              </div>

              {showFaceAmountPopup && (
                <div
                  data-itemid='face-amounts-popup'
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    width: '100%',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '10px',
                    marginTop: '-2px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    color: '#495057',
                    fontSize: '14px',
                    zIndex: 10,
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  Enter amount and press Enter or comma to add multiple values
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* When solving for Face Amount, show Premium Amount - single value */}
          <div style={styles.formGroup} data-itemid='premium-life-wrapper'>
            <div style={styles.row} data-itemid='premium-life-label-wrapper'>
              <label style={styles.label} data-itemid='premium-life-label'>
                Premium Amount
              </label>
              <label style={{ ...styles.label, color: 'red' }} data-itemid='premium-life-label-required'>
                *
              </label>
            </div>
            <input
              data-itemid='premium-life-input'
              onBlur={e => {
                if (formData.premiumLife) {
                  e.target.value = `$${parseInt(formData.premiumLife).toLocaleString()}`;
                }
              }}
              onChange={e => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                onInputChange('premiumLife', numericValue);
              }}
              onFocus={e => {
                if (formData.premiumLife) {
                  e.target.value = formData.premiumLife;
                }
              }}
              placeholder='Enter premium amount...'
              style={styles.input}
              type='text'
              value={formData.premiumLife ? `$${parseInt(formData.premiumLife).toLocaleString()}` : ''}
            />
          </div>
        </>
      )}

      <div style={styles.formGroup} data-itemid='premium-mode-wrapper'>
        <div style={styles.row} data-itemid='premium-mode-label-wrapper'>
          <label style={styles.label} data-itemid='premium-mode-label'>
            Premium Mode
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='premium-mode-label-required'>
            *
          </label>
        </div>
        <select onChange={e => onInputChange('premiumMode', e.target.value)} style={styles.select} value={formData.premiumMode || 'Annual'} data-itemid='premium-mode-select'>
          <option value='Annual' data-itemid='premium-mode-annual'>
            Annual
          </option>
          <option value='Semi-Annual' data-itemid='premium-mode-semi-annual'>
            Semi-Annual
          </option>
          <option value='Quarterly' data-itemid='premium-mode-quarterly'>
            Quarterly
          </option>
          <option value='Monthly' data-itemid='premium-mode-monthly'>
            Monthly
          </option>
        </select>
      </div>

      {/* Flat Extra */}
      <div style={styles.formGroup} data-itemid='flat-extra-wrapper'>
        <label style={styles.label} data-itemid='flat-extra-label'>
          Flat Extra (per thousand)
        </label>
        <input
          data-itemid='flat-extra-input'
          onBlur={e => {
            if (formData.flatExtra) {
              e.target.value = `$${parseFloat(formData.flatExtra).toFixed(2)}`;
            }
          }}
          onChange={e => {
            const rawInput = e.target.value;
            const valueToProcess = rawInput.startsWith('$') ? rawInput.substring(1) : rawInput;
            const cleanValue = valueToProcess.replace(/[^\d.]/g, '');
            const parts = cleanValue.split('.');
            const value = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
            onInputChange('flatExtra', value);
          }}
          onFocus={e => {
            if (formData.flatExtra && e.target.value.startsWith('$')) {
              e.target.value = formData.flatExtra;
            }
          }}
          placeholder='$0.00'
          style={styles.input}
          type='text'
          value={formData.flatExtra ? `$${parseFloat(formData.flatExtra).toFixed(2)}` : '$0.00'}
        />
      </div>

      {/* Optional Riders */}
      <div style={styles.formGroup} data-itemid='optional-riders-wrapper'>
        <label style={styles.label} data-itemid='optional-riders-label'>
          Optional Riders
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }} data-itemid='optional-riders-button-group'>
          <button onClick={() => onInputChange('adb_rider', !formData.adb_rider)} style={styles.riderButton(!!formData.adb_rider)} type='button' data-itemid='adb-rider-button'>
            Accidental Death Benefit
            {formData.adb_rider && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} />}
          </button>

          <button onClick={() => onInputChange('wop_rider', !formData.wop_rider)} style={styles.riderButton(!!formData.wop_rider)} type='button' data-itemid='wop-rider-button'>
            Waiver of Premium
            {formData.wop_rider && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} />}
          </button>
        </div>
      </div>

      {/* Child Rider Units */}
      <div style={styles.formGroup} data-itemid='child-rider-units-wrapper'>
        <label style={styles.label} data-itemid='child-rider-units-label'>
          Child Rider Units
        </label>
        <Select
          value={formData.child_rider_units || '0'}
          onChange={e => onInputChange('child_rider_units', e.target.value)}
          options={Array.from({ length: 26 }, (_, i) => ({ value: String(i), label: String(i) }))}
          placeholder='Select units...'
          data-itemid='child-rider-units-select'
        />
      </div>

      {/* IUL-Specific Fields */}
      {hasIULSelected && (
        <>
          {/* Death Benefit Option */}
          <div style={styles.formGroup} data-itemid='death-benefit-option-wrapper'>
            <label style={styles.label} data-itemid='death-benefit-option-label'>
              Death Benefit Option
              <sup style={{ color: '#d32f2f', fontSize: '10px', marginLeft: '2px' }}>*</sup>
            </label>
            <div style={styles.buttonGroup} data-itemid='death-benefit-option-button-group'>
              {['Level', 'Increasing'].map(option => (
                <button
                  key={option}
                  onClick={() => onInputChange('death_benefit_option', option)}
                  style={styles.button(formData.death_benefit_option === option)}
                  type='button'
                  data-itemid={`death-benefit-option-${option.toLowerCase()}-button`}
                >
                  {option}
                  {formData.death_benefit_option === option && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} />}
                </button>
              ))}
            </div>
          </div>

          {/* Annual Premium - Required for IUL */}
          <div style={styles.formGroup} data-itemid='annual-premium-wrapper'>
            <label style={styles.label} data-itemid='annual-premium-label'>
              Annual Premium
              <sup style={{ color: '#d32f2f', fontSize: '10px', marginLeft: '2px' }}>*</sup>
            </label>
            <input
              onBlur={e => {
                if (formData.annual_premium_iul) {
                  e.target.value = `$${parseInt(formData.annual_premium_iul).toLocaleString()}`;
                }
              }}
              onChange={e => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                onInputChange('annual_premium_iul', numericValue);
              }}
              onFocus={e => {
                if (formData.annual_premium_iul) {
                  e.target.value = formData.annual_premium_iul;
                }
              }}
              placeholder='Enter annual premium...'
              style={styles.input}
              type='text'
              value={formData.annual_premium_iul ? `$${parseInt(formData.annual_premium_iul).toLocaleString()}` : ''}
              data-itemid='annual-premium-input'
            />
          </div>

          {/* To Age - Required when Annual Premium is present */}
          <div style={styles.formGroup} data-itemid='to-age-wrapper'>
            <label style={styles.label} data-itemid='to-age-label'>
              To Age
              <sup style={{ color: '#d32f2f', fontSize: '10px', marginLeft: '2px' }}>*</sup>
            </label>
            <input
              onChange={e => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                onInputChange('to_age_iul', numericValue);
              }}
              placeholder='Enter target age...'
              data-itemid='to-age-input'
              style={styles.input}
              type='text'
              value={formData.to_age_iul || ''}
            />
          </div>

          {/* Initial Premium - Optional */}
          <div style={styles.formGroup} data-itemid='initial-premium-wrapper'>
            <label style={styles.label} data-itemid='initial-premium-label'>
              Initial Premium (Optional)
            </label>
            <input
              onBlur={e => {
                if (formData.initial_premium_iul) {
                  e.target.value = `$${parseInt(formData.initial_premium_iul).toLocaleString()}`;
                }
              }}
              onChange={e => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                onInputChange('initial_premium_iul', numericValue);
              }}
              onFocus={e => {
                if (formData.initial_premium_iul) {
                  e.target.value = formData.initial_premium_iul;
                }
              }}
              placeholder='Enter initial premium...'
              style={styles.input}
              type='text'
              value={formData.initial_premium_iul ? `$${parseInt(formData.initial_premium_iul).toLocaleString()}` : ''}
              data-itemid='initial-premium-input'
            />
          </div>

          {/* One-Time Premium - Optional */}
          <div style={styles.formGroup} data-itemid='one-time-premium-wrapper'>
            <label style={styles.label} data-itemid='one-time-premium-label'>
              One-Time Premium (Optional)
            </label>
            <input
              onBlur={e => {
                if (formData.one_time_premium_iul) {
                  e.target.value = `$${parseInt(formData.one_time_premium_iul).toLocaleString()}`;
                }
              }}
              onChange={e => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                onInputChange('one_time_premium_iul', numericValue);
              }}
              onFocus={e => {
                if (formData.one_time_premium_iul) {
                  e.target.value = formData.one_time_premium_iul;
                }
              }}
              placeholder='Enter one-time premium...'
              style={styles.input}
              type='text'
              value={formData.one_time_premium_iul ? `$${parseInt(formData.one_time_premium_iul).toLocaleString()}` : ''}
              data-itemid='one-time-premium-input'
            />
          </div>

          {/* Face Amount Type - Required for IUL */}
          <div style={styles.formGroup} data-itemid='face-amount-type-wrapper'>
            <label style={styles.label} data-itemid='face-amount-type-label'>
              Face Amount Type
              <sup style={{ color: '#d32f2f', fontSize: '10px', marginLeft: '2px' }}>*</sup>
            </label>
            <div style={styles.buttonGroup} data-itemid='face-amount-type-button-group'>
              {['Minimum', 'Maximum', 'Specified'].map(option => {
                const isSelected = formData.face_amount_type_iul === option;
                return (
                  <button
                    data-itemid={`face-amount-type-button-${option.toLowerCase()}`}
                    key={option}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Update face amount type
                      onInputChange('face_amount_type_iul', option);
                      // Clear specified amount if switching away from 'Specified'
                      if (option !== 'Specified' && formData.specified_face_amount_iul) {
                        // Use setTimeout to ensure state update happens after the first one
                        setTimeout(() => {
                          onInputChange('specified_face_amount_iul', '');
                        }, 0);
                      }
                    }}
                    style={{
                      padding: '7px 14px',
                      border: `1px solid ${isSelected ? '#2d3748' : '#d1d5db'}`,
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#2d3748' : '#fff',
                      color: isSelected ? '#fff' : '#374151',
                      fontSize: '13px',
                      fontWeight: '400',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      justifyContent: 'center',
                      pointerEvents: 'auto'
                    }}
                    type='button'
                  >
                    {option}
                    {isSelected && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Specified Face Amount - Only shown when 'Specified' is selected */}
          {formData.face_amount_type_iul === 'Specified' && (
            <div style={styles.formGroup} data-itemid='specified-face-amount-wrapper'>
              <label style={styles.label} data-itemid='specified-face-amount-label'>
                Specified Face Amount
                <sup style={{ color: '#d32f2f', fontSize: '10px', marginLeft: '2px' }}>*</sup>
              </label>
              <input
                data-itemid='specified-face-amount-input'
                onBlur={e => {
                  if (formData.specified_face_amount_iul) {
                    e.target.value = `$${parseInt(formData.specified_face_amount_iul).toLocaleString()}`;
                  }
                }}
                onChange={e => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  onInputChange('specified_face_amount_iul', numericValue);
                }}
                onFocus={e => {
                  if (formData.specified_face_amount_iul) {
                    e.target.value = formData.specified_face_amount_iul;
                  }
                }}
                placeholder='Enter face amount...'
                style={styles.input}
                type='text'
                value={formData.specified_face_amount_iul ? `$${parseInt(formData.specified_face_amount_iul).toLocaleString()}` : ''}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

LifePlanSection.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  hasQuoteByPremium: PropTypes.bool,
  showFaceAmountPopup: PropTypes.bool.isRequired,
  setShowFaceAmountPopup: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired
};

export default LifePlanSection;
